import type { PersonalizedFertilizerPdpResponse } from "@halm-club/halm-shared";
import { useMemo } from "react";
import { createPortal } from "react-dom";
import { ProductInformation } from "../../../shared/pdp/components/sections/ProductInformation";
import { ObjectionReasons } from "../../../shared/pdp/components/sections/ObjectionReasons";
import { ReviewSection } from "../../../shared/pdp/components/sections/ReviewSection";
import { FertilizerBuyBox } from "./sections/FertilizerBuyBox";
import { FertilizerTimeline } from "./sections/FertilizerTimeline";
import { SoilAnalysisSelector } from "./sections/SoilAnalysisSelector";
import {
  getLawnSizePlaceholderValues,
  replaceInformationItemPlaceholders,
} from "../../../shared/pdp/utils/placeholders";
import { getSelectedFertilizerVariant } from "../purchaseOptions";
import "./PersonalizedFertilizerPdpLayout.css";

type PersonalizedFertilizerPdpLayoutProps = {
  fertilizer: PersonalizedFertilizerPdpResponse;
  fertilizerCompareAtPrice: number;
  fertilizerPrice: number;
  isAddingToCart: boolean;
  isSkipVisible: boolean;
  onAddToCart: () => void;
  onSkip: () => void;
  onSoilAnalysisTypeChange: (type: "AI" | "LAB") => void;
  onLawnSizeChange: (lawnSize: number) => void;
  objectionReasonsPortalTarget: HTMLElement | null;
  reviewContentPortalTarget: HTMLElement | null;
  selectedSoilAnalysisType: "AI" | "LAB";
  selectedLawnSize: number;
  statusMessage: string;
  timelinePortalTarget: HTMLElement | null;
  totalCompareAtPrice: number;
  totalPrice: number;
};

export function PersonalizedFertilizerPdpLayout({
  fertilizer,
  fertilizerCompareAtPrice,
  fertilizerPrice,
  isAddingToCart,
  isSkipVisible,
  onAddToCart,
  onSkip,
  onSoilAnalysisTypeChange,
  onLawnSizeChange,
  objectionReasonsPortalTarget,
  reviewContentPortalTarget,
  selectedSoilAnalysisType,
  selectedLawnSize,
  statusMessage,
  timelinePortalTarget,
  totalCompareAtPrice,
  totalPrice,
}: PersonalizedFertilizerPdpLayoutProps) {
  const selectedSoilAnalysisVariant = fertilizer.soilAnalysisVariants.find(
    (variant) => variant.type === selectedSoilAnalysisType,
  );
  const selectedVariant = getSelectedFertilizerVariant(
    fertilizer.variants,
    selectedLawnSize,
  );
  const currentNavigationStep = fertilizer.navigationSteps?.find(
    (step) => step.isCurrentPage,
  );
  const informationItems = useMemo(
    () =>
      replaceInformationItemPlaceholders(
        fertilizer.collapsibleInformation,
        getLawnSizePlaceholderValues(selectedVariant?.lawnSize),
      ),
    [fertilizer.collapsibleInformation, selectedVariant?.lawnSize],
  );
  const objectionReasons = (
    <ObjectionReasons objectionReasons={fertilizer.objectionReasons} />
  );
  const timeline = (
    <FertilizerTimeline
      timelineSectionCopy={fertilizer.timelineSectionCopy}
      timelineSteps={selectedSoilAnalysisVariant?.timelineSteps}
    />
  );
  const reviewContent = (
    <ReviewSection reviewContent={fertilizer.reviewContent} />
  );

  return (
    <div className="react-pdp__onetime-page personalized-fertilizer-pdp__page">
      <div className="react-pdp__onetime-panel personalized-fertilizer-pdp__panel">
        <FertilizerBuyBox
          buyboxFeatures={fertilizer.buyboxFeatures}
          compareAtPrice={fertilizerCompareAtPrice}
          description={fertilizer.description}
          imageSources={fertilizer.imageSources}
          onLawnSizeChange={onLawnSizeChange}
          price={fertilizerPrice}
          selectedLawnSize={selectedLawnSize}
          title={fertilizer.title}
          variants={fertilizer.variants}
        >
          <SoilAnalysisSelector
            buttonSubText={currentNavigationStep?.navigationDescription}
            compareAtPrice={totalCompareAtPrice}
            informationItems={informationItems}
            isAddingToCart={isAddingToCart}
            isSkipVisible={isSkipVisible}
            onAddToCart={onAddToCart}
            onSkip={onSkip}
            onTypeChange={onSoilAnalysisTypeChange}
            price={totalPrice}
            selectedType={selectedSoilAnalysisType}
            soilAnalysisVariants={fertilizer.soilAnalysisVariants}
            statusMessage={statusMessage}
          />
        </FertilizerBuyBox>

        <ProductInformation
          title={fertilizer.title}
          informationItems={informationItems}
        />

        {timelinePortalTarget
          ? createPortal(timeline, timelinePortalTarget)
          : timeline}
        {reviewContentPortalTarget
          ? createPortal(reviewContent, reviewContentPortalTarget)
          : reviewContent}

        {objectionReasonsPortalTarget
          ? createPortal(objectionReasons, objectionReasonsPortalTarget)
          : objectionReasons}
      </div>
    </div>
  );
}
